import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Flex,
  useToast,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Heading,
  Switch,
  VStack,
  ModalCloseButton,
  IconButton,
  Stack,
  Spinner,
} from "@chakra-ui/react";
import { FaLink } from "react-icons/fa";
import LinkModal from "./LinkModel";
import { useMsal } from "@azure/msal-react";
import { useAppContext } from "../../../context/AppContext";
import {
  fetchQuickLinkGroups,
  addQuickLinkGroups,
  updateQuickLinkGroups,
  deleteQuickLinkGroups,
} from "../../../services/AdminServices/QuickLinkGroups";
import { QuickLinksGroupModel } from "../../../models/QuickLinksGroupModel";

const QuickLinkGroups: React.FC = () => {
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [description, setDescription] = useState("");
  const [groups, setGroups] = useState<QuickLinksGroupModel[]>([]); // Store groups
  const [isEditing, setIsEditing] = useState<number | null>(null); // For edit mode
  const [searchTerm, setSearchTerm] = useState(""); // For search bar
  const toast = useToast(); // To show toasts
  const { isOpen, onOpen, onClose } = useDisclosure();
  // for page controlling
  const [currentPage, setCurrentPage] = useState(1); // Track the current page
  const [itemsPerPage, setItemsPerPage] = useState(10); // Items per page

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null); // To keep track of which item to delete

  // api integration
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // tenant id
  const { accessToken, userData } = useAppContext();
  const tenantID= userData?.tenantID || 0;

  const [isLinkModalOpen, setLinkModalOpen] = useState(false);
  const openLinkModal = (groupId: number) => {
    setSelectedGroupId(groupId);
    setLinkModalOpen(true);
  };

  const closeLinkModal = () => setLinkModalOpen(false);

  const [groupQuickLinks, setGroupQuickLinks] = useState<{
    [key: string]: any[];
  }>({});

  const [selectedGroupId, setSelectedGroupId] = useState<number>(0);

  const handleQuickLinkSave = (groupId: number | 0, updatedLinks: any[]) => {
    
    if (groupId) {
      setGroupQuickLinks((prev) => ({
        ...prev,
        [groupId]: updatedLinks,
      }));
      toast({
        position: "top",
        title: "Quick Links Updated",
        description: `Quick links for group ID ${groupId} have been updated.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // crud part
  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        position: "top",
        title: "Group Name is required",
        description: "Please provide a group name.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!accessToken) {
      toast({
        position: "top",
        title: "Authentication Error",
        description: "Access token is missing. Please log in again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditing !== null) {
        
        // UPDATE CODE LOGIC
        const projectLinkGroupID = groups[isEditing].projectLinkGroupID;
        const updatedGroup = {
          projectLinkGroupID,
          name,
          description,
          isActive,
          updatedBy: userData?.userID
        };

        const response = await updateQuickLinkGroups(updatedGroup, accessToken, tenantID);

        if (typeof response === "object") {
          setGroups((prevGroups) =>
            prevGroups.map((group, index) =>
              index === isEditing ? { ...group, ...response } : group
            )
          );
        } else {
          console.warn("Server response is not valid JSON:", response);
          setGroups((prevGroups) =>
            prevGroups.map((group, index) =>
              index === isEditing ? { ...group, ...updatedGroup } : group
            )
          );
        }
        getQuickLinkGroups();
        toast({
          position: "top",
          title: "Group Updated",
          description: `${name} has been updated successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Add new group logic
        const newGroup = { name, description, isActive ,createdBy: userData?.userID};

        const response = await addQuickLinkGroups(newGroup, accessToken, tenantID);

        if (typeof response === "object") {
          setGroups((prev) => [...prev, response]);
        } else {
          
          console.warn("Server response is not valid JSON:", response);
          // setGroups((prev) => [...prev, newGroup]);
        }

        getQuickLinkGroups();

        toast({
          position: "top",
          title: "Group Added",
          description: `${name} has been added successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error("Save Error:", error);
      toast({
        position: "top",
        title: isEditing !== null ? "Update Failed" : "Add Failed",
        description: "An unexpected error occurred.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });

      setError(
        isEditing !== null ? "Failed to update group." : "Failed to add group."
      );
    } finally {
      setLoading(false);
      handleReset();
      onClose();
    }
  };

  const handleReset = () => {
    setName("");
    setDescription("");
    setIsActive(true);
    setIsEditing(null);
  };

  const handleClose = () => {
    handleReset(); // Reset the form fields
    setIsEditing(null); // Reset the editing mode
    onClose(); // Close the modal
  };

  const handleEdit = (projectLinkGroupID: number) => {

    const groupToEdit = groups.find(group => group.projectLinkGroupID === projectLinkGroupID);
    // const groupToEdit = groups[projectLinkGroupID]; // Get from paginated and filtered data
    if (!groupToEdit) {
      console.error("Group not found for ID:", projectLinkGroupID);
      return;
    }
    const originalIndex = groups.findIndex(
      (group) => group.projectLinkGroupID === groupToEdit.projectLinkGroupID
    ); // Find original index in groups array

    setName(groupToEdit.name);
    setDescription(groupToEdit.description);
    setIsActive(groupToEdit.isActive);
    setIsEditing(originalIndex); // Store the original index for correct updating
    onOpen(); // Open the modal for editing
  };

  const openDeleteModal = (projectLinkGroupID: number) => {
    const index = groups.findIndex(group => group.projectLinkGroupID === projectLinkGroupID);
  
    if (index === -1) {
      console.error("User Group not found for ID:", projectLinkGroupID);
      return;
    }
  
    setDeleteIndex(index); // ✅ Use the index
    setIsDeleteModalOpen(true);
  };
  

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteIndex(null); // Clear the index when modal is closed
  };

  // DELETE Handler
  const handleDelete = async () => {
  if (deleteIndex === null) return;

  // Validate required context values
  if (!accessToken || !userData || typeof userData.userID !== "number") {
    toast({
      position: "top",
      title: "Error",
      description: "Cannot delete group.",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
    return;
  }

  setLoading(true);
  setError(null);

  try {
    const id = groups[deleteIndex].projectLinkGroupID;

    await deleteQuickLinkGroups(id, accessToken, tenantID, userData.userID);

    const updatedGroups = groups.filter((_, i) => i !== deleteIndex);
    setGroups(updatedGroups);

    toast({
      position: "top",
      title: "Group Deleted",
      description: "The group has been deleted successfully.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  } catch (error) {
    console.error("Delete Error:", error);
    setError("Failed to delete group.");
    toast({
      position: "top",
      title: "Delete Failed",
      description: "An error occurred while deleting the group.",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  } finally {
    setLoading(false);
    closeDeleteModal();
  }
};


  // pagination logic form here
  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to the first page when items per page changes
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Filter the sites based on the search query
  const filteredSites = groups.filter((site) =>
    site.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination Logic
  const totalSites = filteredSites.length; // Get total number of filtered sites
  const totalPages = Math.ceil(totalSites / itemsPerPage); // Calculate total pages
  const indexOfLastSite = currentPage * itemsPerPage;
  const indexOfFirstSite = indexOfLastSite - itemsPerPage;
  const currentSites = filteredSites.slice(indexOfFirstSite, indexOfLastSite); // Slice the filtered sites

  useEffect(() => {
    // Reset to first page when search term changes
    setCurrentPage(1);
  }, [searchTerm]);

  const getQuickLinkGroups = async () => {
    
    try {
      if (accessToken) {
        // Check if accessToken is not null
        const data = await fetchQuickLinkGroups(accessToken, tenantID);
        setGroups(data);
        console.log("fetchQuickLinkGroups : " ,data);
      } else {
        setError("Access token is missing.");
      }
    } catch (error) {
      setError("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getQuickLinkGroups();
  }, [accessToken]); // Fetch data when accessToken changes


  return (
    <>
      <Flex justify="space-between" align="center" mb={4} p={6}>
        <Heading as="h2"> Project Groups</Heading>

        <Button colorScheme="blue" onClick={onOpen}>
          <i className="bi bi-diagram-3" style={{ marginRight: "8px" }}></i>
          Add Project Group
        </Button>
      </Flex>

      <Box
        p={6}
        shadow="md"
        borderWidth="1px"
        borderRadius={"15px"}
        backgroundColor={"#fff"}
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Flex justify="space-between" align="center" mb={4}>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              maxWidth="200px"
            >
              <option value={10}>10 items per page</option>
              <option value={20}>20 items per page</option>
              <option value={30}>30 items per page</option>
            </Select>
          </Flex>
          <FormControl mb={4} maxWidth="300px">
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by group name"
            />
          </FormControl>
        </Flex>

        <Table variant="simple" className="Table">
          <Thead>
            <Tr>
              <Th>Group Name</Th>
              <Th>Group Description</Th>
              <Th>Project Link</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentSites.map((group, index) => (
              <Tr key={group.projectLinkGroupID}>
                <Td>{group.name}</Td>
                <Td>{group.description}</Td>
                <Td>
                  <IconButton
                    icon={<FaLink />}
                    aria-label="Add Link"
                    onClick={() => openLinkModal(group.projectLinkGroupID)} // Pass the group ID
                    size="sm"
                    colorScheme="green"
                    ml={5}
                  />
                </Td>
                <Td>
                  <span
                    className={`status-box ${
                      group.isActive ? "active" : "inactive"
                    }`}
                  >
                    {group.isActive ? "Active" : "Inactive"}
                  </span>
                </Td>
                <Td>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => handleEdit(group.projectLinkGroupID)}
                    mr={2}
                  >
                    <i className="bi bi-pencil-square"></i>
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={() => openDeleteModal(group.projectLinkGroupID)}
                  >
                    <i className="bi bi-trash3"></i>
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {loading && <Spinner size="lg" />}

        {/* Delete Conformation  model */}
        <Modal isOpen={isDeleteModalOpen} onClose={closeDeleteModal}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete Site</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {deleteIndex !== null && (
                <p>
                  Are you sure you want to delete the project group"{" "}
                  <strong>{groups[deleteIndex].name}</strong>"?
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="red" mr={3} onClick={handleDelete}>
                Yes, Delete
              </Button>
              <Button variant="ghost" onClick={closeDeleteModal}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Modals */}

        <LinkModal
          isOpen={isLinkModalOpen}
          onClose={closeLinkModal}
          groupId={selectedGroupId}
          quickLinks={
            selectedGroupId !== null
              ? groupQuickLinks[selectedGroupId] || []
              : []
          }
          onSave={(updatedLinks) =>
            handleQuickLinkSave(selectedGroupId, updatedLinks)
          }
        />

        {/* Page Info */}
        <Flex justify="space-between" mt={4}>
          <Box>
            <span>
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalSites)} of {totalSites}{" "}
              entries
            </span>
          </Box>

          {/* Pagination */}
          <HStack spacing={4}>
            <Button
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
            >
              Previous
            </Button>

            <Select
              value={currentPage}
              onChange={(e) => handlePageChange(Number(e.target.value))}
              maxWidth="70px"
            >
              {Array.from({ length: totalPages }, (_, index) => (
                <option key={index} value={index + 1}>
                  {index + 1}
                </option>
              ))}
            </Select>

            <Button
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </HStack>
        </Flex>

        {/* Modal for Add/Edit */}
        <Modal isOpen={isOpen} onClose={handleClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {isEditing !== null
                ? "Edit Project Group"
                : "Add Project Group"}
            </ModalHeader>
            <ModalCloseButton />
            <hr style={{ border: "1px solid #black" }} />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                {/* Group Name */}
                <FormControl isRequired>
                  <FormLabel>Project Name</FormLabel>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </FormControl>

                {/* Group Name */}
                <FormControl isRequired>
                  <FormLabel>Project Discription</FormLabel>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </FormControl>

                {/* Active Status */}
                <FormControl>
                  <Stack alignItems="left">
                    <FormLabel mb={0}>Status</FormLabel>
                    <Switch
                      isChecked={isActive}
                      onChange={(e) => setIsActive(!isActive)}
                      colorScheme="blue"
                    />
                  </Stack>
                </FormControl>
              </VStack>
            </ModalBody>
            <hr style={{ border: "1px solid #black" }} />
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleSave}>
                {isEditing !== null ? "Update Project" : "Create Project"}
              </Button>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </>
  );
};

export default QuickLinkGroups;
